/**
 * ToggleElement - Editor element for boolean toggle switches
 *
 * Features:
 * - MUI Switch for boolean values
 * - Label text
 * - Disabled state support
 *
 * Sprint: basic-config-section
 * Phase: 3 - ToggleElement Component (GREEN)
 */

import React, { useCallback } from 'react';
import { Box, Switch, Typography } from '@mui/material';
import { WithGuideHelp } from '@modules/ui-components';
import type { FieldConfig } from '../../types/editorFieldTypes';
import { useEditorGuideService } from '../../contexts';
import { FIELD_GAP_SMALL } from '../../config/ui';

/**
 * Props for ToggleElement component
 */
export interface ToggleElementProps {
  /** Field configuration */
  fieldConfig: FieldConfig;

  /** Current boolean value */
  value: boolean;

  /** Callback when value changes */
  onChange: (value: boolean) => void;

  /** Whether element is disabled */
  disabled?: boolean;
}

/**
 * ToggleElement - Boolean toggle switch with label
 *
 * Layout:
 * [Switch ----O] Label text
 */
export const ToggleElement: React.FC<ToggleElementProps> = ({
  fieldConfig,
  value,
  onChange,
  disabled = false,
}) => {
  // Handle switch change
  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onChange(event.target.checked);
    },
    [onChange]
  );

  // Get guideService from context for WithGuideHelp (null outside EditorGuideProvider)
  const guideService = useEditorGuideService();

  const labelNode = (
    <Typography
      variant="body2"
      sx={{
        color: disabled ? 'text.disabled' : 'text.primary',
      }}
    >
      {fieldConfig.label}
    </Typography>
  );

  return (
    <Box
      data-testid={`toggle-element-${fieldConfig.path}`}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: FIELD_GAP_SMALL,
      }}
    >
      <Switch
        checked={value}
        onChange={handleChange}
        disabled={disabled}
        size="small"
      />
      {fieldConfig.guideKey && guideService ? (
        <WithGuideHelp
          guideKey={fieldConfig.guideKey}
          guideService={guideService}
          data-testid={`guide-anchor-${fieldConfig.guideKey}`}
          position="inline-end"
          iconSize="small"
        >
          {labelNode}
        </WithGuideHelp>
      ) : (
        labelNode
      )}
    </Box>
  );
};

export default ToggleElement;
